import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { build_params, download_export } from '@/lib/list-query';
import type { SortState } from '@/hooks/use-url-state';
import type {
  AdminPaymentDetail,
  PaymentCounts,
  PaymentFilters,
  PaymentListResponse,
} from './types';

/** The tiles. Describes the whole book, so it deliberately ignores the page's filters. */
export function use_payment_counts() {
  return useQuery({
    queryKey: ['admin-payment-counts'],
    queryFn: async () => (await api.get<PaymentCounts>('/admin/payments/counts')).data,
  });
}

export async function export_payments(f: PaymentFilters, sort?: SortState): Promise<void> {
  await download_export(
    '/admin/payments/export',
    build_params(f as Record<string, never>, sort),
    'payments.csv',
  );
}

/**
 * The list, with whatever filters and sort are set.
 *
 * Through `build_params` rather than a hand-written if-chain: the old version enumerated
 * six filters by hand, so a filter added to the type but forgotten here would look alive
 * on screen while sending nothing.
 */
export function use_payment_list(f: PaymentFilters, sort?: SortState) {
  return useQuery({
    queryKey: ['admin-payments', f, sort],
    queryFn: async () => {
      const res = await api.get<PaymentListResponse>('/admin/payments', {
        params: build_params(f as Record<string, never>, sort),
      });
      return res.data;
    },
    placeholderData: keepPreviousData,
  });
}

export function use_payment_detail(id: string | undefined) {
  return useQuery({
    queryKey: ['admin-payment', id],
    queryFn: async () => (await api.get<AdminPaymentDetail>(`/admin/payments/${id}`)).data,
    enabled: !!id,
  });
}

/**
 * Confirm a recorded refund actually reached the customer.
 *
 * Bookkeeping rather than a money movement — the transfer happens in a bank or the
 * gateway's own dashboard, and this records that somebody checked.
 */
export function use_settle_refund(payment_id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { refund_id: string; settlement_ref: string; note?: string }) =>
      (
        await api.post<AdminPaymentDetail>(
          `/admin/payments/${payment_id}/refunds/${input.refund_id}/settle`,
          { settlement_ref: input.settlement_ref, note: input.note },
        )
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-payment', payment_id] });
      void qc.invalidateQueries({ queryKey: ['admin-payments'] });
      // The "still owed" tile and segment both move with this.
      void qc.invalidateQueries({ queryKey: ['admin-payment-counts'] });
    },
  });
}

export function use_refund_payment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { amount_inr: number; reason: string }) =>
      (await api.post<AdminPaymentDetail>(`/admin/payments/${id}/refund`, input)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-payment', id] });
      void qc.invalidateQueries({ queryKey: ['admin-payments'] });
      // The tiles describe the whole book, and a refund has just changed it.
      void qc.invalidateQueries({ queryKey: ['admin-payment-counts'] });
    },
  });
}
